import type { CanvasDrawTool } from '../stores/uiStore';

export type ConnectMode = 'none' | 'once' | 'sticky';

export interface ArmedState {
  drawTool: CanvasDrawTool;
  stampType: string | null;
  sticky: boolean;
  connectMode: ConnectMode;
}

export type DrawChordAction =
  | { kind: 'tool'; tool: CanvasDrawTool }
  | { kind: 'stamp'; type: string };

/** `d` then this key arms a draw/stamp tool. `d d` then the key makes it sticky. */
export const DRAW_CHORD_ACTIONS: Record<string, DrawChordAction> = {
  b: { kind: 'tool', tool: 'box' },
  t: { kind: 'tool', tool: 'text' },
  a: { kind: 'tool', tool: 'arrow' },
  l: { kind: 'tool', tool: 'line' },
  f: { kind: 'tool', tool: 'frame' },
  n: { kind: 'stamp', type: 'note' },
  o: { kind: 'stamp', type: 'oval' },
  r: { kind: 'stamp', type: 'rounded' },
  i: { kind: 'stamp', type: 'diamond' },
  c: { kind: 'stamp', type: 'cylinder' },
  h: { kind: 'stamp', type: 'hexagon' },
  v: { kind: 'stamp', type: 'triangle' },
  x: { kind: 'stamp', type: 'cross' },
};

export const DRAW_CHORD_HINT =
  'd ... b=Box t=Text n=Note o=Oval r=Rounded i=Diamond c=Cylinder h=Hex v=Triangle a=Arrow l=Line f=Frame · dd=sticky';

export const DRAW_STICKY_CHORD_HINT =
  'sticky d ... b=Box t=Text n=Note o=Oval r=Rounded i=Diamond c=Cylinder h=Hex v=Triangle a=Arrow l=Line f=Frame';

export function emptyArmed(): ArmedState {
  return { drawTool: 'none', stampType: null, sticky: false, connectMode: 'none' };
}

export function cycleDraw(state: ArmedState, tool: CanvasDrawTool): ArmedState {
  if (tool === 'none') return { ...emptyArmed(), connectMode: state.connectMode };
  const same = state.drawTool === tool && !state.stampType;
  if (same && !state.sticky) return { ...state, sticky: true, connectMode: 'none' };
  if (same && state.sticky) return emptyArmed();
  return { drawTool: tool, stampType: null, sticky: false, connectMode: 'none' };
}

export function cycleStamp(state: ArmedState, type: string): ArmedState {
  const same = state.stampType === type;
  if (same && !state.sticky) return { ...state, sticky: true, drawTool: 'none', connectMode: 'none' };
  if (same && state.sticky) return emptyArmed();
  return { drawTool: 'none', stampType: type, sticky: false, connectMode: 'none' };
}

export function cycleConnect(state: ArmedState): ArmedState {
  if (state.connectMode === 'none') {
    return { drawTool: 'none', stampType: null, sticky: false, connectMode: 'once' };
  }
  if (state.connectMode === 'once') return { ...state, connectMode: 'sticky', drawTool: 'none', stampType: null };
  return emptyArmed();
}

export function activateDraw(tool: CanvasDrawTool, sticky: boolean): ArmedState {
  if (tool === 'none') return emptyArmed();
  return { drawTool: tool, stampType: null, sticky, connectMode: 'none' };
}

export function activateStamp(type: string, sticky: boolean): ArmedState {
  return { drawTool: 'none', stampType: type, sticky, connectMode: 'none' };
}

export function consumeDraw(state: ArmedState): ArmedState {
  if (state.sticky) return state;
  return { ...state, drawTool: 'none', stampType: null };
}

export function consumeConnect(state: ArmedState): ArmedState {
  if (state.connectMode === 'sticky') return state;
  return { ...state, connectMode: 'none' };
}

export function armModeLabel(active: boolean, sticky: boolean): string {
  if (!active) return '';
  return sticky ? 'sticky' : 'once';
}
